// eslint-disable-next-line no-unused-vars
import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import ChecklistGeneralDataCard from './ChecklistGeneralDataCard';
import { initialState, reducer } from 'features/checklist/reducers/generalData/containerReducer';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { checklistValidator } from 'features/checklist/validators';
import { useToast } from 'hooks/toasts/useToast';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import useLanguageTracker from 'hooks/useLanguageTracker';
import useDialog from 'hooks/useDialog';
import { TARGET_AREAS_QUERY } from 'features/documentTemplates/edit/queries';
import { GENERIC_ENTITY_FLOWS_QUERY, GENERIC_ENTITY_BUSINESS_STATES_FLOW_QUERY, CHECKLIST_TYPES_QUERY } from '../../queries';
import { ADD_UPDATE_CHECKLIST, VERIFY_CHECKLIST } from '../../mutations';
import { CHECKLIST_UPDATED, CHECKLIST_VERIFIED } from '../../subscriptions';
import { set, get, useStateLens } from '@totalsoft/react-state-lens';

const ChecklistGeneralDataContainer = ({ id, data, filters, onChangeChecklistTitle, onRedirect, refetch }) => {
  const addToast = useToast();
  const [t] = useTranslation();

  const [localState, dispatch] = useReducer(reducer, initialState);
  const targerAreaIdIsDirty = useStateLens(false);
  const [saving, setSaving] = useState(false);

  const [checklistValidation, validateChecklist] = useDirtyFieldValidation(checklistValidator);
  const { showDialog, DialogComponent } = useDialog();

  // #region Queries

  const { data: genericEntityFlowsData, refetch: refetchGenericEntityFlows } = useQuery(GENERIC_ENTITY_FLOWS_QUERY, {
    variables: { context: 'checklist' }
  });

  const { data: genericEntityBusinessStatesFlowData } = useQuery(GENERIC_ENTITY_BUSINESS_STATES_FLOW_QUERY, {
    variables: { flowId: localState?.checklist?.flowId },
    skip: !localState?.checklist?.flowId
  });

  const { data: targetAreasData, refetch: refetchTargetAreas } = useQuery(TARGET_AREAS_QUERY);

  const { data: requiredDocTypesData, refetch: refetchRequiredDocTypes } = useQuery(CHECKLIST_TYPES_QUERY);

  // #endregion

  // #region Mutations

  const [addOrUpdateChecklist] = useMutation(ADD_UPDATE_CHECKLIST);

  const [verifyChecklist] = useMutation(VERIFY_CHECKLIST);

  // #endregion

  // #region Subscriptions

  useSubscription(CHECKLIST_UPDATED, {
    onData: data => {
      addToast(t('Checklist.ChecklistUpdated'), 'success');
      const ChecklistId = data.data.data.checklistUpdated.requiredDocumentId;
      dispatch({ type: 'OnHeaderSaved', id: ChecklistId });
      refetch();
      if (id === undefined) onRedirect(ChecklistId);
      setSaving(false);
      (targerAreaIdIsDirty |> set)(false);
    }
  });

  useSubscription(CHECKLIST_VERIFIED, {
    onData: data => {
      const invalidateDocumentTemplates = data.data.data.checklistVerified.invalidateDocumentTemplates;
      if (invalidateDocumentTemplates && invalidateDocumentTemplates.length > 0) {
        showDialog({
          title: t('Checklist.DocumentTemplateValidationTitle'),
          content: t('Checklist.DocumentTemplateValidationText', { list: invalidateDocumentTemplates }),
          onConfirm: handleSave
        });
      } else handleSave();
    }
  });

  // #endregion

  // #region Handlers

  const handlePropertyChanged = (propertyName, value) => {
    if (propertyName === 'targetAreaId') {
      (targerAreaIdIsDirty |> set)(true);
    }
    dispatch({ type: 'OnHeaderPropertyChanged', propertyName, value });
    if (propertyName === 'reqDocTypeId') {
      let requiredDocType = requiredDocTypesData?.requiredDocTypes.find(x => x.id === value);
      dispatch({
        type: 'OnHeaderPropertyChanged',
        propertyName: 'documentName',
        value: requiredDocType.name
      });
      dispatch({
        type: 'OnHeaderPropertyChanged',
        propertyName: 'sharePointContentType',
        value: requiredDocType.sharePointContentType
      });
    }
    if (propertyName === 'canExpire' && value === false) {
      dispatch({
        type: 'OnHeaderPropertyChanged',
        propertyName: 'validityDays',
        value: null
      });
    }
  };

  const handleSaveWrapper = () => {
    if (id !== null && id !== undefined && (targerAreaIdIsDirty |> get)) {
      const input = { id: localState.checklist.id, targetAreaId: localState.checklist.targetAreaId };
      return verifyChecklist({ variables: { input } });
    }

    handleSave();
  };

  const handleSave = updateDocumentTemplates => {
    /* eslint-disable no-unused-vars */
    const {
      changed,
      documentName,
      documentCode,
      sharePointContentType,
      filterCondition,
      targetAreaName,
      requiredDocTypeXUserGroups,
      ...reqDoc
    } = localState.checklist;
    /* eslint-enable no-unused-vars */
    const input = {
      ...reqDoc,
      numberConditions: filters.map(x => x.orderNo),
      updateDocumentTemplates: updateDocumentTemplates
    };

    if (!validateChecklist(input)) return;
    setSaving(true);
    return addOrUpdateChecklist({ variables: { input } });
  };

  // #endregion

  // #region Side effects

  const onLanguageChange = useCallback(() => {
    refetchGenericEntityFlows();
    refetchTargetAreas();
    refetchRequiredDocTypes();
  }, [refetchGenericEntityFlows, refetchTargetAreas, refetchRequiredDocTypes]);

  useLanguageTracker({
    onChange: onLanguageChange
  });

  useEffect(() => {
    var reqDoc = {
      ...localState.checklist,
      numberConditions: filters.map(x => x.orderNo)
    };
    validateChecklist(reqDoc);
  }, [filters, localState.checklist, validateChecklist]);

  useEffect(() => {
    if (data) dispatch({ type: 'OnInitState', data });
  }, [data]);

  useEffect(() => {
    onChangeChecklistTitle(localState?.checklist?.documentName);
  }, [localState?.checklist?.documentName, onChangeChecklistTitle]);

  // #endregion

  return (
    <>
      <ChecklistGeneralDataCard
        checklist={localState?.checklist}
        targetAreas={targetAreasData ? targetAreasData.targetAreas : []}
        requiredDocTypes={requiredDocTypesData ? requiredDocTypesData.requiredDocTypes : []}
        genericEntityFlows={genericEntityFlowsData ? genericEntityFlowsData.genericEntityFlowsDictionary : []}
        genericEntityBusinessStates={
          genericEntityBusinessStatesFlowData ? genericEntityBusinessStatesFlowData.genericEntityBusinessStatesXFlow : []
        }
        onSave={handleSaveWrapper}
        onPropertyChanged={handlePropertyChanged}
        saving={saving}
        validation={checklistValidation}
        id={id}
      />
      {DialogComponent}
    </>
  );
};

ChecklistGeneralDataContainer.propTypes = {
  id: PropTypes.number,
  data: PropTypes.object,
  filters: PropTypes.array,
  onChangeChecklistTitle: PropTypes.func,
  onRedirect: PropTypes.func,
  refetch: PropTypes.func
};

export default ChecklistGeneralDataContainer;
